import * as React from 'react'

import { SearchIcon } from 'lucide-react'
import { action, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import MenuCloser from '~/client/src/shared/components/MenuCloser'
import { IFilters } from '~/client/src/shared/stores/InitialState'

import EntityNameFilterStore from './EntityNameFilter.store'

// localization: translated

const doneTypingInterval = 300
const SEARCH_SIZE = 18

interface IEntityNameFilter {
  filters: IFilters
  onShowChanged?: (isShown: boolean) => void
  placeholder?: string
  className?: string
  forceClose?: boolean
  openOnLeft?: boolean
  testId?: string
}

@observer
export default class EntityNameFilter extends React.Component<IEntityNameFilter> {
  public static defaultProps = {
    className: '',
  }

  @observable private typingTimer: number
  private readonly store: EntityNameFilterStore

  public constructor(props: IEntityNameFilter) {
    super(props)

    this.store = new EntityNameFilterStore(props.filters, props.onShowChanged)
    makeObservable(this)
  }

  public componentDidUpdate() {
    if (this.props.forceClose) {
      this.store.isShown = false
    }
  }

  public render() {
    const isActive = this.store.isShown || !!this.store.searchKey.length
    const { className, testId } = this.props

    return (
      <MenuCloser
        key={'search'}
        className={classList({
          'tw:border tw:border-solid': true,
          [className]: !!className,
          'tw:border-primary tw:bg-primary/10': isActive,
          'tw:border-border tw:bg-background': !isActive,
        })}
        closeMenu={this.store.close}
      >
        <div className="tw:flex tw:items-center">
          <span
            data-testid={testId}
            onClick={this.store.toggle}
            className="tw:flex tw:items-center tw:justify-center tw:ml-1 tw:shrink-0 tw:cursor-pointer"
          >
            <SearchIcon size={SEARCH_SIZE} />
          </span>
          {this.store.isShown ? (
            <input
              className="tw:bg-transparent tw:mx-2 tw:outline-none tw:border-0 tw:p-0 tw:max-w-[80vw] tw:h-full tw:flex-1 tw:transition-[max-width] tw:duration-[400ms]"
              type="search"
              autoFocus={true}
              value={this.store.searchKey}
              onChange={this.updateSearchKey}
              onKeyDown={this.keyDownAction}
              onKeyUp={this.keyUpAction}
              data-testid={testId ? `${testId}-input` : undefined}
            />
          ) : (
            <div
              className={classList({
                'tw:text-body-md tw:text-foreground tw:flex-1': true,
                'tw:text-primary tw:mx-2': isActive,
              })}
              onClick={this.store.toggle}
            >
              {this.store.searchKey}
            </div>
          )}
        </div>
      </MenuCloser>
    )
  }

  @action.bound
  private updateSearchKey(event: React.SyntheticEvent<HTMLInputElement>) {
    this.store.searchKey = event.currentTarget.value
  }

  private keyUpAction = () => {
    clearTimeout(this.typingTimer)

    this.typingTimer = window.setTimeout(this.store.update, doneTypingInterval)
  }

  private keyDownAction = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      this.store.update()
      this.store.close()
    }
    clearTimeout(this.typingTimer)
  }
}
